import React, { useEffect, useState, useCallback } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function EmailsTab({ groupId }) {
  const { t } = useTranslation();

  const [emailLogs, setEmailLogs] = useState([]);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailForm, setEmailForm] = useState({
    subject: '',
    message: '',
    filter_type: 'all',
  });

  const fetchEmails = useCallback(
    async (signal) => {
      try {
        const res = await api.get(`/groups/${groupId}/emails/logs`);
        if (!signal?.aborted) setEmailLogs(res.data || []);
      } catch {
        if (!signal?.aborted) {
          toast.error(t('groupAdminTabs.emails.toastLoadError'));
        }
      }
    },
    [groupId, t]
  );

  const handleSendEmail = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post(`/groups/${groupId}/emails/send`, emailForm);
      toast.success(res.data?.message || 'Email queued!');
      setShowEmailDialog(false);
      setEmailForm({ subject: '', message: '', filter_type: 'all' });
      const controller = new AbortController();
      fetchEmails(controller.signal);
    } catch {
      toast.error(t('groupAdminTabs.emails.toastSendError'));
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    const id = setTimeout(() => {
      fetchEmails(controller.signal);
    }, 0);

    return () => {
      controller.abort();
      clearTimeout(id);
    };
  }, [fetchEmails]);

  const getFilterLabel = (filterType) => {
    if (filterType === 'active')
      return t('groupAdminTabs.emails.filterActive');
    if (filterType === 'expired')
      return t('groupAdminTabs.emails.filterExpired');
    return t('groupAdminTabs.emails.filterAll');
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">
          {t('groupAdminTabs.emails.title')}
        </h2>
        <Button onClick={() => setShowEmailDialog(true)}>
          <Mail size={16} className="mr-2" />
          {t('groupAdminTabs.emails.sendButton')}
        </Button>
      </div>

      {emailLogs.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-slate-500">
            {t('groupAdminTabs.emails.none')}
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {emailLogs.map((log) => (
            <Card key={log.id}>
              <CardHeader>
                <CardTitle>{log.subject}</CardTitle>
                <CardDescription>
                  {log.created_at
                    ? format(new Date(log.created_at), 'PPP p')
                    : ''}
                </CardDescription>
              </CardHeader>
              <CardContent className="text-sm text-slate-600">
                <p className="line-clamp-3">{log.message}</p>
                <div className="mt-2">
                  <Badge variant="outline">
                    {getFilterLabel(log.filter_type || 'all')}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('groupAdminTabs.emails.dialogTitle')}
            </DialogTitle>
            <DialogDescription>
              {t('groupAdminTabs.emails.dialogDescription')}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSendEmail} className="space-y-3">
            <div>
              <Label>{t('groupAdminTabs.emails.subject')}</Label>
              <Input
                value={emailForm.subject}
                onChange={(e) =>
                  setEmailForm((f) => ({ ...f, subject: e.target.value }))
                }
                required
              />
            </div>
            <div>
              <Label>{t('groupAdminTabs.emails.message')}</Label>
              <Textarea
                rows={6}
                value={emailForm.message}
                onChange={(e) =>
                  setEmailForm((f) => ({ ...f, message: e.target.value }))
                }
                required
              />
            </div>
            <div>
              <Label>{t('groupAdminTabs.emails.filter')}</Label>
              <Select
                value={emailForm.filter_type}
                onValueChange={(v) =>
                  setEmailForm((f) => ({ ...f, filter_type: v }))
                }
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={t(
                      'groupAdminTabs.emails.filterPlaceholder'
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    {t('groupAdminTabs.emails.filterAll')}
                  </SelectItem>
                  <SelectItem value="active">
                    {t('groupAdminTabs.emails.filterActive')}
                  </SelectItem>
                  <SelectItem value="expired">
                    {t('groupAdminTabs.emails.filterExpired')}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowEmailDialog(false)}
              >
                {t('groupAdminTabs.emails.cancel')}
              </Button>
              <Button type="submit">
                {t('groupAdminTabs.emails.send')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
